// เนื้อเรื่องปะการัง ดอกไม้ทะเล แมงกะพรุน ดาวทะเล เม่นทะเล และปลิงทะเล
// รวมฉากผสมพันธุ์แบบปล่อยไข่ลงน้ำ (broadcastSpawn) ที่ใช้กับหอยสองฝาด้วย

import type { GameEvent } from "../../engine/types";

const SE = ["SESSILE"] as const;
const E = ["ECHINODERM"] as const;
const CORAL = ["cnidarian.coral"];
const JELLY = ["cnidarian.jellyfish"];
const BOX_JELLIES = ["Chirodropidae", "Chiropsalmidae", "Carybdeidae", "Alatinidae", "Tripedaliidae"];

export const MARINE_INVERTEBRATE_EVENTS: readonly GameEvent[] = [
  // ---------- ปล่อยไข่ลงน้ำ (ปะการังมีฉากคืนพระจันทร์เต็มดวงของตัวเอง) ----------
  {
    id: "spawn_ripen",
    stageTags: ["adult"],
    requires: { broadcastSpawn: true, notSubgroups: CORAL, notFlags: ["has_mate"] },
    weight: 14,
    text: "น้ำรอบตัวอุ่นขึ้นตามฤดู ร่างกายของคุณเต็มไปด้วยไข่หรือสเปิร์มที่พร้อมแล้ว",
    fact: "สัตว์น้ำหลายชนิดผสมพันธุ์ด้วยการปล่อยไข่และสเปิร์มลงน้ำพร้อมกัน การปล่อยพร้อมเพื่อนบ้านช่วยให้ไข่ได้รับการผสมมากขึ้น",
    choices: [
      {
        label: "รอสัญญาณจากเพื่อนบ้าน",
        outcomes: [
          { chance: 0.8, text: "คุณรับรู้สารเคมีจากเพื่อนบ้านที่เริ่มปล่อยไข่แล้ว ถึงเวลาของคุณ", effects: { addFlags: ["has_mate"] } },
          { chance: 0.2, text: "ปีนี้เพื่อนบ้านเงียบผิดปกติ คุณรอต่อไป", effects: { fullness: -5 } },
        ],
      },
      {
        label: "ปล่อยก่อนใคร",
        outcomes: [
          { chance: 0.5, text: "เพื่อนบ้านปล่อยตามคุณมาติดๆ", effects: { addFlags: ["has_mate"] } },
          { chance: 0.5, text: "ไม่มีใครปล่อยตาม ไข่ของคุณลอยหายไปเปล่าๆ", effects: { fullness: -10 } },
        ],
      },
    ],
  },
  {
    id: "spawn_release",
    stageTags: ["adult"],
    requires: { broadcastSpawn: true, notSubgroups: CORAL, flags: ["has_mate"] },
    weight: 30,
    text: "น้ำรอบตัวขุ่นไปด้วยไข่และสเปิร์มจากเพื่อนบ้าน ถึงเวลาแล้ว",
    choices: [
      { label: "ปล่อยไข่ทั้งหมดออกไป", outcomes: [{ chance: 1, text: "คุณปล่อยไข่ราว {n} ฟองลอยออกไปกับกระแสน้ำ", effects: { offspring: "clutch", fullness: -20, removeFlags: ["has_mate"], addFlags: ["parent"] } }] },
      {
        label: "ปล่อยเป็นระลอกตามจังหวะน้ำ",
        outcomes: [
          { chance: 0.8, text: "ไข่ราว {n} ฟองกระจายออกไปพร้อมกับกระแสน้ำขึ้น", effects: { offspring: "clutch", fullness: -15, removeFlags: ["has_mate"], addFlags: ["parent"] } },
          { chance: 0.2, text: "ปลาฝูงหนึ่งมาเก็บกินไข่ แต่ก็ยังเหลือราว {n} ฟองที่ลอยรอดไปได้", effects: { offspring: "clutch", fullness: -15, safety: -10, removeFlags: ["has_mate"], addFlags: ["parent"] } },
        ],
      },
    ],
  },

  // ---------- ตัวอ่อนพลานูลา ----------
  {
    id: "planula_drift",
    archetypes: SE,
    stages: ["planula"],
    weight: 8,
    text: "คุณเป็นตัวอ่อนรูปไข่ มีขนเล็กๆ รอบตัว ว่ายหมุนไปในน้ำเพื่อหาที่ลงเกาะ",
    fact: "ตัวอ่อนพลานูลาของปะการังและแมงกะพรุนหลายชนิดกินอาหารไม่ได้ ต้องใช้พลังงานที่แม่เก็บไว้ให้ จึงต้องรีบหาที่ลงเกาะ",
    choices: [
      {
        label: "จมลงหาพื้นหินแข็ง",
        outcomes: [
          { chance: 0.7, text: "คุณเจอพื้นหินสะอาดที่เหมาะกับการเริ่มต้นชีวิต", effects: { safety: 10 } },
          { chance: 0.3, text: "พื้นข้างล่างมีแต่ตะกอนโคลน คุณต้องว่ายต่อจนเหนื่อย", effects: { health: -10 } },
        ],
      },
      {
        label: "ลอยไปกับกระแสอีกสักพัก",
        outcomes: [
          { chance: 0.6, text: "กระแสน้ำพาคุณไปถึงแนวหินที่ยังว่างอยู่", effects: { move: "near", safety: 5 } },
          { chance: 0.4, text: "ปลาที่กินแพลงก์ตอนกวาดตัวอ่อนรอบตัวคุณเข้าปาก คุณรอดมาได้อย่างหวุดหวิด", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "planula_reef_sound",
    archetypes: SE,
    stages: ["planula"],
    requires: { subgroups: CORAL },
    weight: 5,
    text: "เสียงกรอบแกรบของกุ้งและปลาดังมาจากทางหนึ่ง",
    fact: "ตัวอ่อนปะการังว่ายเข้าหาเสียงของแนวปะการังที่สมบูรณ์ได้",
    choices: [
      {
        label: "ว่ายตามเสียงไป",
        outcomes: [
          { chance: 0.8, text: "เสียงนั้นพาคุณไปถึงแนวปะการังที่มีชีวิตชีวา", effects: { safety: 10 } },
          { chance: 0.2, text: "ยิ่งใกล้แนวปะการัง ยิ่งมีปลารอกินตัวอ่อน", effects: { health: -20, cause: "predator" } },
        ],
      },
      {
        label: "ปล่อยตัวลอยต่อไป",
        outcomes: [
          { chance: 0.5, text: "คุณลอยไปเจอโขดหินว่างๆ อีกฝั่ง" },
          { chance: 0.5, text: "คุณลอยห่างจากแนวปะการังออกไปเรื่อยๆ", effects: { health: -15 } },
        ],
      },
    ],
  },
  {
    id: "polyp_nudibranch",
    archetypes: SE,
    stages: ["polyp"],
    weight: 4,
    text: "ทากทะเลสีสดตัวหนึ่งคลานเข้ามาหาคุณช้าๆ",
    fact: "ทากทะเลบางชนิดกินโพลิป แล้วเก็บเข็มพิษที่ยังไม่ได้ยิงไว้ใช้ป้องกันตัวเอง",
    choices: [
      {
        label: "ยิงเข็มพิษใส่",
        outcomes: [
          { chance: 0.5, text: "ทากทะเลถอยหนีไป", effects: { safety: 5 } },
          { chance: 0.5, text: "ทากทะเลไม่สะทกสะท้าน มันกินเนื้อเยื่อของคุณไปพร้อมเข็มพิษ", effects: { health: -25, cause: "predator" } },
        ],
      },
      { label: "หดตัวให้เล็กที่สุด", outcomes: [{ chance: 1, text: "ทากทะเลกัดโดนนิดหน่อยแล้วคลานไปหาโพลิปตัวอื่น", effects: { health: -10 } }] },
    ],
  },

  // ---------- ปะการังและดอกไม้ทะเล ----------
  {
    id: "coral_algae",
    opening: true,
    archetypes: SE,
    stages: ["polyp"],
    requires: { subgroups: CORAL },
    weight: 10,
    text: "สาหร่ายเซลล์เดียวสีน้ำตาลทองลอยเข้ามา และเริ่มอาศัยอยู่ในเนื้อเยื่อของคุณ",
    fact: "ปะการังได้อาหารส่วนใหญ่จากสาหร่ายเซลล์เดียวที่อาศัยอยู่ในเนื้อเยื่อ สีน้ำตาลเขียวของปะการังก็มาจากสาหร่ายพวกนี้",
    choices: [
      { label: "รับไว้เป็นเพื่อนร่วมบ้าน", outcomes: [{ chance: 1, text: "สาหร่ายสังเคราะห์แสงแล้วแบ่งน้ำตาลให้คุณ คุณให้ที่อยู่และของเสียเป็นปุ๋ยแก่มัน", effects: { fullness: 30, health: 5 } }] },
      {
        label: "รอสาหร่ายสายพันธุ์อื่น",
        outcomes: [
          { chance: 0.5, text: "สายพันธุ์ที่มาทีหลังทนร้อนได้ดีกว่า", effects: { fullness: 25, health: 10 } },
          { chance: 0.5, text: "รอนานจนอ่อนแรง กว่าสาหร่ายจะมา", effects: { fullness: 10, health: -5 } },
        ],
      },
    ],
  },
  {
    id: "coral_skeleton",
    archetypes: SE,
    stages: ["polyp", "adult"],
    requires: { subgroups: CORAL },
    weight: 5,
    text: "น้ำทะเลใสและอุ่นกำลังดี เหมาะกับการเติบโต",
    fact: "แนวปะการังเกิดจากโครงหินปูนของปะการังจำนวนมหาศาลที่สะสมกันมานานหลายพันปี",
    choices: [
      { label: "สร้างโครงหินปูนให้สูงขึ้น", outcomes: [{ chance: 1, text: "คุณสูงขึ้นจนพ้นตะกอนที่พื้น", effects: { safety: 10 } }] },
      { label: "แตกกิ่งออกด้านข้าง", outcomes: [{ chance: 1, text: "กิ่งใหม่ได้รับแสงมากขึ้น", effects: { fullness: 15, safety: 5 } }] },
    ],
  },
  {
    id: "coral_sunlight",
    archetypes: SE,
    stages: ["polyp", "adult", "elder"],
    requires: { subgroups: CORAL },
    weight: 8,
    text: "แดดส่องลงมาถึงแนวปะการังเต็มที่ น้ำใสจนมองเห็นพื้นทรายชัดเจน",
    choices: [
      { label: "กางโพลิปรับแสง", outcomes: [{ chance: 1, text: "สาหร่ายในเนื้อเยื่อของคุณสังเคราะห์แสงสร้างอาหารให้ทั้งวัน", effects: { fullness: 30 } }] },
      {
        label: "หดโพลิปกันตะกอน",
        outcomes: [
          { chance: 0.7, text: "ตะกอนจากคลื่นไม่ทับตัวคุณ และคุณยังได้แสงบ้าง", effects: { fullness: 15, health: 5 } },
          { chance: 0.3, text: "หดตัวนานไป สาหร่ายสร้างอาหารได้น้อย", effects: { fullness: 5 } },
        ],
      },
    ],
  },
  {
    id: "coral_night_feed",
    archetypes: SE,
    stages: ["polyp", "adult", "elder"],
    requires: { subgroups: CORAL },
    weight: 7,
    text: "พลบค่ำแล้ว แพลงก์ตอนสัตว์ลอยขึ้นมาจากที่ลึกเต็มไปหมด",
    fact: "ปะการังหลายชนิดยื่นหนวดที่มีเข็มพิษออกมาจับแพลงก์ตอนตอนกลางคืน",
    choices: [
      {
        label: "ยื่นหนวดออกมาดักจับ",
        outcomes: [
          { chance: 0.8, text: "เข็มพิษบนหนวดจับแพลงก์ตอนได้ทีละตัวไม่หยุด", effects: { fullness: 30 } },
          { chance: 0.2, text: "ปลาผีเสื้อมาจิกกินหนวดของคุณ", effects: { fullness: 10, health: -10, cause: "predator" } },
        ],
      },
      { label: "หดหนวดไว้ในโครงหินปูน", outcomes: [{ chance: 1, text: "คุณพึ่งอาหารจากสาหร่ายอย่างเดียวคืนนี้", effects: { fullness: 10, safety: 5 } }] },
    ],
  },
  {
    id: "coral_bleaching",
    archetypes: SE,
    stages: ["adult", "elder"],
    requires: { subgroups: CORAL },
    weight: 3,
    threatenedWeight: 2,
    text: "น้ำทะเลร้อนผิดปกติต่อเนื่องมาหลายสัปดาห์",
    fact: "เมื่อน้ำทะเลร้อนเกินไป ปะการังจะขับสาหร่ายในเนื้อเยื่อออกจนขาวซีด ถ้าน้ำไม่เย็นลงก็อาจตาย",
    choices: [
      {
        label: "อดทนรอให้น้ำเย็นลง",
        outcomes: [
          { chance: 0.5, text: "น้ำเย็นลงทันเวลา สาหร่ายค่อยๆ กลับมา", effects: { health: -15 } },
          { chance: 0.4, text: "คุณขาวซีดไปทั้งอาณานิคม และอ่อนแรงลงมาก", effects: { health: -40, cause: "disaster" } },
          { chance: 0.1, text: "น้ำร้อนอยู่นานเกินไป อาณานิคมของคุณค่อยๆ ตายลงทั้งหมด", effects: { death: "disaster" } },
        ],
      },
      {
        label: "จับแพลงก์ตอนกินให้มากขึ้นชดเชย",
        outcomes: [
          { chance: 0.6, text: "อาหารจากหนวดช่วยประคองชีวิตคุณไว้จนน้ำเย็นลง", effects: { health: -10, fullness: 15 } },
          { chance: 0.4, text: "แพลงก์ตอนมีไม่พอ คุณอ่อนแรงลงทุกวัน", effects: { health: -35, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "coral_storm",
    archetypes: SE,
    stages: ["adult", "elder"],
    requires: { subgroups: CORAL },
    weight: 3,
    text: "พายุใหญ่ซัดคลื่นกระหน่ำแนวปะการังทั้งคืน",
    fact: "กิ่งปะการังที่หักแล้วไปตกในที่เหมาะสม เติบโตเป็นอาณานิคมใหม่ได้ นักอนุรักษ์จึงใช้วิธีนี้ปลูกปะการัง",
    choices: [
      {
        label: "ยึดหินไว้ให้แน่น",
        outcomes: [
          { chance: 0.6, text: "คุณเสียกิ่งไปไม่กี่กิ่ง", effects: { health: -10 } },
          { chance: 0.3, text: "กิ่งของคุณหักกระจาย แต่บางกิ่งไปตกบนหินแล้วเติบโตเป็นอาณานิคมใหม่ {n} กอ", effects: { offspringCount: 3, health: -20, cause: "disaster" } },
          { chance: 0.1, text: "ทั้งอาณานิคมถูกคลื่นพลิกคว่ำจมทราย", effects: { death: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "coral_cots",
    archetypes: SE,
    stages: ["adult", "elder"],
    requires: { subgroups: CORAL },
    weight: 2.5,
    text: "ดาวมงกุฎหนามหลายสิบตัวคลานมาตามแนวปะการัง ทิ้งโครงหินปูนขาวโพลนไว้ข้างหลัง",
    fact: "ปูตัวเล็กที่อาศัยในกิ่งปะการังบางชนิดช่วยหนีบไล่ดาวมงกุฎหนามออกไป",
    choices: [
      {
        label: "พึ่งปูเฝ้าบ้านในกิ่งของคุณ",
        outcomes: [
          { chance: 0.6, text: "ปูตัวจิ๋วหนีบเท้าท่อของดาวมงกุฎหนามจนมันถอยไป", effects: { safety: 5 } },
          { chance: 0.4, text: "ดาวมงกุฎหนามตัวใหญ่เกินกว่าปูจะไล่ไหว มันกินเนื้อเยื่อคุณไปส่วนหนึ่ง", effects: { health: -30, cause: "predator" } },
        ],
      },
      { label: "ทนให้มันกินส่วนปลายแล้วงอกใหม่", outcomes: [{ chance: 1, text: "คุณเสียกิ่งปลายไปหลายกิ่ง แต่ฐานยังอยู่", effects: { health: -20, cause: "predator" } }] },
    ],
  },
  {
    id: "coral_parrotfish",
    archetypes: SE,
    stages: ["adult", "elder"],
    requires: { subgroups: CORAL },
    weight: 4,
    text: "ฝูงปลานกแก้วว่ายเข้ามาขูดกินสาหร่ายที่ขึ้นคลุมตัวคุณ",
    fact: "ปลานกแก้วขูดกินสาหร่ายบนปะการัง แล้วถ่ายเศษหินปูนออกมาเป็นทรายขาวละเอียด",
    choices: [
      {
        label: "ปล่อยให้มันทำความสะอาด",
        outcomes: [
          { chance: 0.8, text: "สาหร่ายที่บังแสงหายไป คุณได้รับแสงเต็มที่อีกครั้ง", effects: { health: 10, fullness: 5 } },
          { chance: 0.2, text: "ปากแข็งเหมือนนกแก้วของมันขูดโดนเนื้อปะการังไปด้วย", effects: { health: -10 } },
        ],
      },
    ],
  },
  {
    id: "coral_divers",
    archetypes: SE,
    stages: ["adult", "elder"],
    requires: { subgroups: CORAL },
    weight: 2,
    threatenedWeight: 2,
    text: "เรือนำเที่ยวมาจอดเหนือแนวปะการัง นักดำน้ำหลายคนกำลังลงมา",
    choices: [
      {
        label: "หวังว่าพวกเขาจะระวัง",
        outcomes: [
          { chance: 0.6, text: "นักดำน้ำลอยตัวชมคุณอยู่ห่างๆ ถ่ายรูปแล้วก็จากไป" },
          { chance: 0.3, text: "ตีนกบของนักดำน้ำมือใหม่ปัดกิ่งของคุณหัก", effects: { health: -15, cause: "human" } },
          { chance: 0.1, text: "สมอเรืออีกลำกระแทกลงมากลางอาณานิคม", effects: { health: -40, cause: "human" } },
        ],
      },
    ],
  },
  {
    id: "coral_moon",
    archetypes: SE,
    stages: ["adult"],
    requires: { subgroups: CORAL, notFlags: ["has_mate"] },
    weight: 14,
    text: "อีกไม่กี่คืนจะเป็นคืนพระจันทร์เต็มดวง และน้ำทะเลก็อุ่นขึ้นตามฤดู",
    fact: "ปะการังหลายชนิดปล่อยไข่และสเปิร์มพร้อมกันทั้งแนวในคืนเดียวของปี หลังคืนพระจันทร์เต็มดวงไม่กี่วัน",
    choices: [
      {
        label: "เตรียมห่อไข่และสเปิร์มให้พร้อม",
        outcomes: [
          { chance: 0.85, text: "ก้อนสีชมพูเล็กๆ เริ่มดันขึ้นมาที่ปากโพลิปทุกตัว", effects: { fullness: -10, addFlags: ["has_mate"] } },
          { chance: 0.15, text: "ปีนี้คุณสะสมพลังงานไม่พอ ต้องรอปีหน้า", effects: { fullness: -5 } },
        ],
      },
    ],
  },
  {
    id: "coral_mass_spawn",
    archetypes: SE,
    stages: ["adult"],
    requires: { subgroups: CORAL, flags: ["has_mate"] },
    weight: 30,
    text: "คืนนี้ทั้งแนวปะการังปล่อยไข่ออกมาพร้อมกัน เหมือนหิมะลอยขึ้นจากพื้นทะเล",
    choices: [
      { label: "ปล่อยห่อไข่ขึ้นสู่ผิวน้ำ", outcomes: [{ chance: 1, text: "ไข่ราว {n} ฟองจากตัวคุณลอยขึ้นไปผสมกับสเปิร์มจากปะการังกอข้างเคียง", effects: { offspring: "clutch", fullness: -20, removeFlags: ["has_mate"], addFlags: ["parent"] } }] },
    ],
  },
  {
    id: "anemone_clownfish",
    archetypes: SE,
    stages: ["polyp", "adult", "elder"],
    requires: { subgroups: CORAL, families: ["Actiniidae", "Stichodactylidae"] },
    weight: 6,
    text: "ปลาการ์ตูนคู่หนึ่งว่ายวนเข้ามาใกล้หนวดของคุณ",
    fact: "ปลาการ์ตูนมีเมือกพิเศษที่ทำให้เข็มพิษของดอกไม้ทะเลไม่ยิงใส่ ทั้งคู่จึงอยู่ด้วยกันได้ ปลาช่วยไล่ปลาที่มากินหนวด",
    choices: [
      { label: "ให้มันเข้ามาอยู่ด้วย", outcomes: [{ chance: 1, text: "ปลาการ์ตูนไล่ปลาผีเสื้อที่มากินหนวดคุณ และของเสียของมันก็เป็นปุ๋ย", effects: { safety: 15, fullness: 10 } }] },
      { label: "ยิงเข็มพิษไล่", outcomes: [{ chance: 1, text: "เข็มพิษไม่ทำอะไรมันเลย ปลาการ์ตูนยังว่ายวนอยู่ใกล้ๆ", effects: { fullness: -5 } }] },
    ],
  },

  // ---------- แมงกะพรุน ----------
  {
    id: "jelly_polyp_clone",
    archetypes: SE,
    stages: ["polyp"],
    requires: { subgroups: JELLY },
    weight: 7,
    text: "คุณเกาะอยู่ใต้โขดหินในที่ร่ม และเริ่มแตกหน่อเป็นโพลิปตัวใหม่ข้างๆ",
    fact: "โพลิปแมงกะพรุนตัวเดียวแตกหน่อเพิ่มจำนวนได้เรื่อยๆ และแต่ละตัวยังผลิตลูกแมงกะพรุนได้อีกหลายตัว",
    choices: [
      { label: "แตกหน่อเพิ่ม", outcomes: [{ chance: 1, text: "โพลิปพี่น้อง {n} ตัวงอกขึ้นรอบตัวคุณ ทุกตัวมียีนเหมือนคุณ", effects: { offspringCount: 3, fullness: -10 } }] },
      { label: "เก็บพลังงานไว้รอฤดูน้ำอุ่น", outcomes: [{ chance: 1, text: "คุณจับแพลงก์ตอนกินและรอเวลา", effects: { fullness: 20 } }] },
    ],
  },
  {
    id: "jelly_ephyra",
    archetypes: SE,
    stages: ["ephyra"],
    requires: { subgroups: JELLY },
    weight: 8,
    text: "คุณเป็นแมงกะพรุนตัวจิ๋วรูปดาวแปดแฉก หุบร่มพาตัวเองไปข้างหน้าทีละนิด",
    choices: [
      { label: "จับแพลงก์ตอนสัตว์ให้ได้มากที่สุด", outcomes: [{ chance: 1, text: "คุณโตขึ้นทุกวัน แฉกเริ่มเชื่อมกันเป็นร่ม", effects: { fullness: 30 } }] },
      { label: "ลอยต่ำหลบปลา", outcomes: [{ chance: 1, text: "ข้างล่างอาหารน้อยกว่า แต่ปลอดภัยกว่า", effects: { safety: 10, fullness: 10 } }] },
    ],
  },
  {
    id: "jelly_sting",
    archetypes: SE,
    stages: ["adult"],
    requires: { subgroups: JELLY },
    weight: 8,
    text: "ฝูงปลาตัวเล็กว่ายผ่านมาใต้ร่มของคุณ",
    fact: "เซลล์เข็มพิษของแมงกะพรุนยิงออกเร็วมาก นับเป็นกลไกที่เร็วที่สุดอย่างหนึ่งในธรรมชาติ",
    choices: [
      {
        label: "ปล่อยหนวดยาวลงไป",
        outcomes: [
          { chance: 0.8, text: "ปลาว่ายชนหนวดของคุณ เข็มพิษนับพันยิงพร้อมกัน", effects: { fullness: 35 } },
          { chance: 0.2, text: "ฝูงปลาเลี้ยวหลบทัน", effects: { fullness: 5 } },
        ],
      },
      { label: "ลอยนิ่งรอเหยื่อตัวเล็กกว่า", outcomes: [{ chance: 1, text: "แพลงก์ตอนลอยมาติดหนวดเรื่อยๆ", effects: { fullness: 20 } }] },
    ],
  },
  {
    id: "jelly_turtle",
    archetypes: SE,
    stages: ["ephyra", "adult"],
    requires: { subgroups: JELLY },
    weight: 4,
    text: "เต่าทะเลตัวมหึมาว่ายตรงมาทางคุณ",
    fact: "เต่ามะเฟืองกินแมงกะพรุนเป็นอาหารหลัก ในคอมีหนามแข็งช่วยกลืน และบางครั้งก็กินถุงพลาสติกเพราะคิดว่าเป็นแมงกะพรุน",
    choices: [
      {
        label: "หุบร่มดำลงลึก",
        outcomes: [
          { chance: 0.6, text: "เต่าว่ายผ่านไปงับแมงกะพรุนตัวอื่น", effects: { safety: -5 } },
          { chance: 0.4, text: "เต่างับคุณทั้งตัว", effects: { death: "predator" } },
        ],
      },
      {
        label: "ยิงเข็มพิษสู้",
        outcomes: [
          { chance: 0.2, text: "เต่าสะดุ้งแล้วหันไปทางอื่น" },
          { chance: 0.8, text: "ปากและคอของเต่ามีหนามรองรับ เข็มพิษไม่ระคายมันเลย", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "jelly_beach",
    archetypes: SE,
    stages: ["adult"],
    requires: { subgroups: JELLY },
    weight: 3,
    text: "ลมมรสุมพัดฝูงแมงกะพรุนเข้าหาฝั่ง",
    choices: [
      {
        label: "หุบร่มสู้กระแส",
        outcomes: [
          { chance: 0.6, text: "คุณว่ายสวนกระแสออกไปได้ แม้จะเหนื่อย", effects: { fullness: -10 } },
          { chance: 0.4, text: "คุณเกยหาดตอนน้ำลง กว่าน้ำจะขึ้นมาก็เกือบแห้ง", effects: { health: -30, cause: "disaster" } },
        ],
      },
      {
        label: "ปล่อยตัวตามคลื่น",
        outcomes: [
          { chance: 0.3, text: "คลื่นพาคุณไปเข้าอ่าวที่มีแพลงก์ตอนเยอะ", effects: { move: "near", fullness: 25 } },
          { chance: 0.5, text: "คุณเกยหาดอยู่ครึ่งวัน", effects: { health: -30, cause: "disaster" } },
          { chance: 0.2, text: "คุณเกยหาดกลางแดดจัด และไม่ได้กลับลงทะเลอีก", effects: { death: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "jelly_fishery",
    archetypes: SE,
    stages: ["adult"],
    requires: { subgroups: JELLY },
    weight: 2,
    text: "ชาวประมงกำลังใช้สวิงช้อนแมงกะพรุนขึ้นเรือทีละตัว",
    fact: "แมงกะพรุนบางชนิดเป็นอาหารของคน คนไทยรู้จักกันในชื่อแมงกะพรุนหนัง",
    choices: [
      {
        label: "หุบร่มดำลงลึก",
        outcomes: [
          { chance: 0.7, text: "คุณจมลงพ้นระยะสวิง", effects: { fullness: -5 } },
          { chance: 0.3, text: "สวิงช้อนคุณขึ้นไปพร้อมกับตัวอื่นๆ", effects: { death: "human" } },
        ],
      },
    ],
  },
  {
    id: "jelly_immortal",
    archetypes: SE,
    stages: ["adult"],
    requires: { subgroups: JELLY, families: ["Oceaniidae"] },
    once: true,
    weight: 10,
    text: "ร่างกายของคุณเริ่มแก่และบาดเจ็บ แต่บางอย่างข้างในกำลังเปลี่ยนไป",
    fact: "แมงกะพรุนอมตะ Turritopsis dohrnii ย้อนกลับไปเป็นโพลิปได้เมื่อบาดเจ็บหรือแก่ แล้วเริ่มวงจรชีวิตใหม่",
    choices: [
      { label: "ย้อนวัยกลับไปเป็นโพลิป", outcomes: [{ chance: 1, text: "ร่มของคุณหดเล็กลง จมลงเกาะพื้น กลายเป็นโพลิป แล้วแตกหน่อเป็นแมงกะพรุนตัวใหม่ {n} ตัว ทุกตัวคือคุณอีกครั้ง", effects: { offspringCount: 5, health: 40 } }] },
      { label: "ใช้ชีวิตต่อไปตามปกติ", outcomes: [{ chance: 1, text: "คุณลอยต่อไปในทะเลอุ่น", effects: { fullness: 10 } }] },
    ],
  },
  {
    id: "box_jelly_eyes",
    archetypes: SE,
    stages: ["adult"],
    requires: { subgroups: JELLY, families: BOX_JELLIES },
    weight: 7,
    text: "รากไม้ชายเลนเป็นเงามืดอยู่ข้างหน้า มีกุ้งตัวเล็กซ่อนอยู่เต็มไปหมด",
    fact: "แมงกะพรุนกล่องมีตา 24 ดวง บางดวงมีเลนส์ จึงว่ายหลบสิ่งกีดขวางและเลือกทิศทางได้ แม้จะไม่มีสมอง",
    choices: [
      { label: "ว่ายหลบรากไม้เข้าไปล่า", outcomes: [{ chance: 1, text: "ตาของคุณพาว่ายหลบรากไม้ได้ทุกราก คุณจับกุ้งได้หลายตัว", effects: { fullness: 35 } }] },
      { label: "ล่าในที่โล่ง", outcomes: [{ chance: 1, text: "ที่โล่งมีปลาตัวเล็กให้ล่าไม่มากนัก", effects: { fullness: 20 } }] },
    ],
  },
  {
    id: "box_jelly_swimmer",
    archetypes: SE,
    stages: ["adult"],
    requires: { subgroups: JELLY, families: BOX_JELLIES },
    weight: 2,
    text: "นักท่องเที่ยวกลุ่มหนึ่งลงเล่นน้ำทะเลตอนพลบค่ำ",
    fact: "พิษของแมงกะพรุนกล่องบางชนิดร้ายแรงถึงชีวิตคน ชายหาดหลายแห่งในไทยจึงมีน้ำส้มสายชูไว้ราดแผลเพื่อปฐมพยาบาล",
    choices: [
      { label: "ว่ายหลบออกไป", outcomes: [{ chance: 1, text: "ตาของคุณเห็นเงาคนก่อน คุณว่ายห่างออกไปได้", effects: { safety: 5 } }] },
      {
        label: "ลอยต่อไปตามเดิม",
        outcomes: [
          { chance: 0.5, text: "หนวดของคุณพาดโดนขาของคนหนึ่ง เขาร้องลั่น และหนวดคุณขาดติดไปหลายเส้น", effects: { health: -15, cause: "human" } },
          { chance: 0.5, text: "พวกเขาขึ้นจากน้ำไปก่อนจะเจอคุณ" },
        ],
      },
    ],
  },

  // ---------- ดาวทะเลและพวกพ้องทั่วไป ----------
  {
    id: "echino_larva",
    archetypes: E,
    stages: ["larva"],
    weight: 8,
    text: "คุณลอยอยู่ในทะเล แขนใสๆ ที่มีขนเล็กๆ คอยพัดอาหารเข้าปาก",
    fact: "ตัวอ่อนของดาวทะเลมีสมมาตรซ้ายขวาแบบเดียวกับเรา ก่อนจะเปลี่ยนเป็นห้าแฉกตอนโต",
    choices: [
      { label: "พัดแพลงก์ตอนพืชเข้าปาก", outcomes: [{ chance: 1, text: "คุณกินจนตัวพองใส", effects: { fullness: 30 } }] },
      { label: "ว่ายลงลึกหลบปลา", outcomes: [{ chance: 1, text: "ข้างล่างปลอดภัยกว่า แต่อาหารน้อย", effects: { safety: 10, fullness: 5 } }] },
    ],
  },
  {
    id: "echino_tube_feet",
    archetypes: E,
    stages: ["juvenile"],
    weight: 7,
    text: "เท้าเล็กๆ นับร้อยใต้ตัวคุณค่อยๆ ยืดออกยึดพื้นทีละข้าง",
    fact: "ดาวทะเลและเม่นทะเลเดินด้วยเท้าท่อนับร้อย ที่ขับเคลื่อนด้วยแรงดันน้ำในร่างกาย",
    choices: [
      { label: "เดินสำรวจพื้นทะเล", outcomes: [{ chance: 1, text: "คุณเจอสาหร่ายและเศษอาหารบนพื้นทราย", effects: { fullness: 25 } }] },
      { label: "เกาะนิ่งในซอกหิน", outcomes: [{ chance: 1, text: "ซอกหินแคบเกินกว่าปลาจะเข้ามาได้", effects: { safety: 10, fullness: 5 } }] },
    ],
  },
  {
    id: "echino_crab",
    archetypes: E,
    stages: ["juvenile"],
    weight: 5,
    text: "ปูตัวเล็กตัวหนึ่งคลานเข้ามาหาคุณที่ตัวยังนิ่มอยู่",
    choices: [
      {
        label: "ซุกใต้ก้อนหิน",
        outcomes: [
          { chance: 0.8, text: "ปูหาคุณไม่เจอ", effects: { safety: 5 } },
          { chance: 0.2, text: "ปูยื่นก้ามเข้ามาถึงตัวคุณ", effects: { health: -25, cause: "predator" } },
        ],
      },
      {
        label: "อยู่นิ่งๆ",
        outcomes: [
          { chance: 0.5, text: "ปูเดินผ่านไปเฉยๆ" },
          { chance: 0.5, text: "ปูหนีบโดนตัวคุณ", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },

  // ---------- ดาวทะเล ----------
  {
    id: "starfish_stomach",
    archetypes: E,
    stages: ["adult", "elder"],
    requires: { subgroups: ["echinoderm.starfish"], notFamilies: ["Acanthasteridae"] },
    weight: 8,
    text: "หอยแมลงภู่กลุ่มหนึ่งเกาะอยู่บนโขดหินข้างหน้า",
    fact: "ดาวทะเลกินหอยโดยดันกระเพาะออกทางปากเข้าไปในเปลือกหอย แล้วย่อยอาหารนอกตัว",
    choices: [
      {
        label: "คร่อมแล้วดึงเปลือกให้แง้ม",
        outcomes: [
          { chance: 0.85, text: "หอยทนแรงดึงไม่ไหว เปลือกแง้มออกนิดเดียวก็พอให้กระเพาะคุณเข้าไป", effects: { fullness: 40 } },
          { chance: 0.15, text: "หอยตัวนี้ปิดเปลือกแน่นกว่าที่คิด คุณดึงจนเหนื่อย", effects: { fullness: 5 } },
        ],
      },
      { label: "กินซากที่พื้นทะเลแทน", outcomes: [{ chance: 1, text: "ซากปลาบนพื้นทรายเป็นมื้อที่ง่ายกว่า", effects: { fullness: 20 } }] },
    ],
  },
  {
    id: "starfish_regen",
    archetypes: E,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["echinoderm.starfish"] },
    weight: 5,
    text: "ปลาวัวตัวหนึ่งงับแขนข้างหนึ่งของคุณไว้แน่น",
    fact: "ดาวทะเลงอกแขนที่ขาดใหม่ได้ บางชนิดแขนที่ขาดยังงอกเป็นตัวใหม่ได้ ถ้ามีส่วนของจานกลางติดไปด้วย",
    choices: [
      { label: "สลัดแขนทิ้ง", outcomes: [{ chance: 1, text: "คุณหนีไปได้ แขนใหม่จะค่อยๆ งอกขึ้นมาในอีกหลายเดือน", effects: { health: -15, safety: 5 } }] },
      {
        label: "ดึงสู้",
        outcomes: [
          { chance: 0.4, text: "ปลาวัวปล่อยแขนคุณเพราะหนามแหลม", effects: { health: -10 } },
          { chance: 0.6, text: "ปลาวัวกระชากจนคุณบาดเจ็บหนัก", effects: { health: -30, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "cots_coral",
    archetypes: E,
    stages: ["adult", "elder"],
    requires: { subgroups: ["echinoderm.starfish"], families: ["Acanthasteridae"] },
    weight: 8,
    text: "เบื้องหน้าคือปะการังเขากวางกอใหญ่",
    fact: "ดาวมงกุฎหนามกินปะการังเป็นอาหาร เมื่อระบาดจำนวนมากจะทำลายแนวปะการังได้เป็นบริเวณกว้าง",
    choices: [
      { label: "ดันกระเพาะออกมาคลุมปะการัง", outcomes: [{ chance: 1, text: "คุณย่อยเนื้อปะการังจนเหลือแต่โครงหินปูนขาว", effects: { fullness: 40 } }] },
      { label: "กินเฉพาะตอนกลางคืน", outcomes: [{ chance: 1, text: "ความมืดช่วยซ่อนคุณจากนักดำน้ำและหอยสังข์แตร", effects: { fullness: 30, safety: 5 } }] },
    ],
  },
  {
    id: "cots_divers",
    archetypes: E,
    stages: ["adult", "elder"],
    requires: { subgroups: ["echinoderm.starfish"], families: ["Acanthasteridae"] },
    weight: 3,
    text: "นักดำน้ำถือเข็มฉีดยาว่ายตรงมาทางกลุ่มดาวมงกุฎหนามที่คุณอยู่",
    fact: "เมื่อดาวมงกุฎหนามระบาด นักอนุรักษ์จะดำน้ำลงไปกำจัดทีละตัว เพื่อปกป้องแนวปะการัง",
    choices: [
      {
        label: "คลานหนีเข้าซอกปะการัง",
        outcomes: [
          { chance: 0.6, text: "เขาหาคุณไม่เจอในซอกลึก", effects: { safety: 5 } },
          { chance: 0.4, text: "เข็มฉีดยาเอื้อมมาถึงตัวคุณ", effects: { death: "human" } },
        ],
      },
      {
        label: "อยู่นิ่งๆ ในที่เดิม",
        outcomes: [
          { chance: 0.2, text: "เขาฉีดตัวอื่นจนน้ำยาหมด แล้วขึ้นจากน้ำไป" },
          { chance: 0.8, text: "คุณคือตัวต่อไป", effects: { death: "human" } },
        ],
      },
    ],
  },
  {
    id: "starfish_wasting",
    archetypes: E,
    stages: ["adult", "elder"],
    requires: { subgroups: ["echinoderm.starfish"] },
    weight: 2,
    text: "แขนของคุณเริ่มมีแผลขาวๆ และอ่อนเปลี้ย",
    fact: "โรคดาวทะเลซูบผอมเคยระบาดตามชายฝั่งแปซิฟิกของอเมริกาเหนือ ทำให้ดาวทะเลตายไปหลายพันล้านตัว",
    choices: [
      {
        label: "ย้ายไปที่น้ำเย็นกว่า",
        outcomes: [
          { chance: 0.6, text: "น้ำเย็นช่วยชะลออาการ แผลค่อยๆ หาย", effects: { move: "near", health: -10, fullness: -10 } },
          { chance: 0.4, text: "อาการลุกลามไปถึงแขนข้างอื่น", effects: { health: -35, cause: "disease" } },
        ],
      },
      {
        label: "อยู่นิ่งๆ พักฟื้น",
        outcomes: [
          { chance: 0.4, text: "ร่างกายของคุณสู้กับโรคได้", effects: { health: -5 } },
          { chance: 0.6, text: "แผลขยายใหญ่ขึ้นทุกวัน", effects: { health: -30, cause: "disease" } },
        ],
      },
    ],
  },

  // ---------- เม่นทะเล ----------
  {
    id: "urchin_graze",
    archetypes: E,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["echinoderm.urchin"] },
    weight: 8,
    text: "พื้นหินรอบตัวมีสาหร่ายขึ้นเป็นพรม",
    fact: "ปากของเม่นทะเลมีฟันห้าซี่เรียงเป็นโครงสร้างที่เรียกว่าตะเกียงของอริสโตเติล",
    choices: [
      { label: "ขูดกินสาหร่ายบนหิน", outcomes: [{ chance: 1, text: "ฟันห้าซี่ขูดสาหร่ายเข้าปากจนหินเกลี้ยง", effects: { fullness: 35 } }] },
      { label: "กินเฉพาะตอนกลางคืน", outcomes: [{ chance: 1, text: "ตอนกลางวันคุณซ่อนในซอกหิน ตอนกลางคืนค่อยออกมากิน", effects: { fullness: 25, safety: 5 } }] },
    ],
  },
  {
    id: "urchin_triggerfish",
    archetypes: E,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["echinoderm.urchin"] },
    weight: 6,
    text: "ปลาวัวตัวหนึ่งว่ายวนอยู่รอบตัวคุณ หาจังหวะพลิกคุณหงาย",
    fact: "ปลาวัวบางชนิดพ่นน้ำพลิกเม่นทะเลให้หงาย แล้วกัดกินด้านล่างที่หนามสั้นกว่า",
    choices: [
      {
        label: "ชี้หนามไปทางมัน",
        outcomes: [
          { chance: 0.7, text: "หนามยาวแหลมทำให้ปลาวัวไม่กล้าเข้าใกล้", effects: { safety: 5 } },
          { chance: 0.3, text: "ปลาวัวพ่นน้ำพลิกคุณหงาย แล้วกัดโดนด้านล่าง", effects: { health: -30, cause: "predator" } },
        ],
      },
      {
        label: "ยึดตัวเข้าซอกหิน",
        outcomes: [
          { chance: 0.85, text: "เท้าท่อยึดคุณไว้แน่นในซอก ปลาวัวพลิกคุณไม่ได้", effects: { fullness: -5 } },
          { chance: 0.15, text: "ปลาวัวกัดหนามคุณหักทีละอันจนถึงตัว", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "urchin_cover",
    archetypes: E,
    stages: ["juvenile", "adult"],
    requires: { subgroups: ["echinoderm.urchin"] },
    weight: 4,
    text: "แดดส่องลงมาจ้าในน้ำตื้น",
    fact: "เม่นทะเลบางชนิดใช้เท้าท่อหยิบเปลือกหอยและเศษสาหร่ายมาคลุมตัว เพื่อบังแสงและพรางตัว",
    choices: [
      { label: "หยิบเปลือกหอยมาบังตัว", outcomes: [{ chance: 1, text: "คุณดูเหมือนกองเศษเปลือกหอยมากกว่าเม่นทะเล", effects: { safety: 10 } }] },
      { label: "คลานลงที่ลึกกว่า", outcomes: [{ chance: 1, text: "ข้างล่างแสงอ่อนกว่า แต่สาหร่ายก็น้อยกว่า", effects: { safety: 5, fullness: 5 } }] },
    ],
  },
  {
    id: "urchin_harvest",
    archetypes: E,
    stages: ["adult", "elder"],
    requires: { subgroups: ["echinoderm.urchin"] },
    weight: 2,
    threatenedWeight: 2,
    text: "นักดำน้ำคนหนึ่งถือตะกร้าไว้มือหนึ่ง อีกมือถือคีม",
    fact: "ไข่เม่นทะเลหรืออูนิเป็นอาหารราคาแพงในหลายประเทศ",
    choices: [
      {
        label: "ซุกเข้าซอกหิน",
        outcomes: [
          { chance: 0.7, text: "คีมเอื้อมเข้ามาไม่ถึง", effects: { safety: 5 } },
          { chance: 0.3, text: "คีมคีบคุณออกมาใส่ตะกร้า", effects: { death: "human" } },
        ],
      },
      {
        label: "ชี้หนามขู่",
        outcomes: [
          { chance: 0.5, text: "นักดำน้ำโดนหนามตำมือ และว่ายไปเก็บตัวอื่นแทน", effects: { safety: 5 } },
          { chance: 0.5, text: "ถุงมือหนากันหนามได้ คุณถูกเก็บใส่ตะกร้า", effects: { death: "human" } },
        ],
      },
    ],
  },

  // ---------- ปลิงทะเล ----------
  {
    id: "cucumber_sand",
    archetypes: E,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["echinoderm.cucumber"] },
    weight: 8,
    text: "พื้นทรายตรงหน้ามีเศษอาหารปะปนอยู่บางๆ",
    fact: "ปลิงทะเลกินทรายแล้วย่อยเอาเศษอาหารออก ก่อนถ่ายทรายสะอาดออกมา จึงช่วยทำความสะอาดพื้นทะเล",
    choices: [
      { label: "กลืนทรายไปเรื่อยๆ", outcomes: [{ chance: 1, text: "คุณกินทรายทั้งวัน และถ่ายออกมาเป็นทรายสะอาด", effects: { fullness: 30 } }] },
      { label: "ยื่นหนวดรอบปากจับเศษอาหารที่ลอยมา", outcomes: [{ chance: 1, text: "หนวดของคุณจับเศษอาหารเข้าปากทีละนิด", effects: { fullness: 25, safety: 5 } }] },
    ],
  },
  {
    id: "cucumber_evisceration",
    archetypes: E,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["echinoderm.cucumber"] },
    weight: 5,
    text: "ปูตัวใหญ่คลานเข้ามาและเริ่มหนีบตัวคุณ",
    fact: "ปลิงทะเลบางชนิดพ่นอวัยวะภายในออกทางก้นเพื่อหนีศัตรู แล้วงอกอวัยวะใหม่ได้ในเวลาไม่กี่สัปดาห์",
    choices: [
      { label: "พ่นไส้ออกมาใส่มัน", outcomes: [{ chance: 1, text: "ปูสาละวนกับไส้ คุณค่อยๆ คืบหนีไป อวัยวะใหม่จะงอกขึ้นมาแทน", effects: { health: -20, fullness: -15, safety: 15 } }] },
      {
        label: "ปล่อยเส้นใยเหนียวพันมัน",
        outcomes: [
          { chance: 0.7, text: "เส้นใยเหนียวพันขาปูจนมันดิ้นไม่หลุด", effects: { safety: 10, fullness: -5 } },
          { chance: 0.3, text: "ปูตัดเส้นใยขาดแล้วหนีบคุณต่อ", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "cucumber_pearlfish",
    archetypes: E,
    stages: ["adult", "elder"],
    requires: { subgroups: ["echinoderm.cucumber"] },
    weight: 3,
    text: "ปลาตัวยาวเรียวตัวหนึ่งว่ายวนอยู่ข้างหลังคุณ แล้วพยายามมุดเข้าทางก้น",
    fact: "ปลามุกบางชนิดอาศัยอยู่ในทวารของปลิงทะเล ปลิงทะเลบางชนิดจึงมีฟันเล็กๆ รอบก้นไว้กันแขกไม่ได้รับเชิญ",
    choices: [
      { label: "ปล่อยให้มันเข้ามาอาศัย", outcomes: [{ chance: 1, text: "ปลามุกได้บ้านใหม่ คุณรู้สึกแปลกๆ แต่ก็ไม่เป็นอะไรมาก", effects: { health: -5 } }] },
      { label: "ปิดก้นไว้", outcomes: [{ chance: 1, text: "ปลามุกว่ายไปหาปลิงทะเลตัวอื่น" }] },
    ],
  },
  {
    id: "cucumber_harvest",
    archetypes: E,
    stages: ["adult", "elder"],
    requires: { subgroups: ["echinoderm.cucumber"] },
    weight: 2.5,
    threatenedWeight: 2,
    text: "ชาวประมงดำน้ำเก็บปลิงทะเลจากพื้นทรายทีละตัว",
    fact: "ปลิงทะเลตากแห้งเป็นอาหารราคาแพง ทำให้ปลิงทะเลหลายชนิดถูกจับจนใกล้สูญพันธุ์",
    choices: [
      {
        label: "มุดลงทราย",
        outcomes: [
          { chance: 0.6, text: "คุณฝังตัวจนเหลือแค่ปลายหนวด ชาวประมงมองไม่เห็น", effects: { fullness: -5 } },
          { chance: 0.4, text: "มือคู่หนึ่งดึงคุณขึ้นจากทราย", effects: { death: "human" } },
        ],
      },
      {
        label: "อยู่นิ่งๆ",
        outcomes: [
          { chance: 0.3, text: "ชาวประมงเก็บตัวที่ใหญ่กว่าคุณไปก่อน" },
          { chance: 0.7, text: "คุณถูกเก็บใส่ถุงตาข่าย", effects: { death: "human" } },
        ],
      },
    ],
  },

  // ---------- ดาวเปราะและพลับพลึงทะเล ----------
  {
    id: "brittlestar_arms",
    archetypes: E,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["echinoderm.other"] },
    weight: 7,
    text: "คุณซ่อนจานกลางไว้ใต้ก้อนหิน ยื่นแขนเรียวยาวออกมากวาดหาเศษอาหาร",
    fact: "ดาวเปราะเคลื่อนที่ได้เร็วที่สุดในบรรดาสัตว์พวกดาวทะเล โดยใช้แขนเรียวยาวพายไปข้างหน้า",
    choices: [
      { label: "กวาดเศษอาหารเข้าหาปาก", outcomes: [{ chance: 1, text: "แขนของคุณส่งเศษอาหารต่อกันเป็นทอดๆ จนถึงปาก", effects: { fullness: 30 } }] },
      {
        label: "พายแขนออกไปหาที่ใหม่",
        outcomes: [
          { chance: 0.7, text: "คุณพายตัวไปได้เร็วกว่าที่ปลาคิด และเจอซากปลา", effects: { move: "near", fullness: 30 } },
          { chance: 0.3, text: "ปลางับแขนคุณไว้ คุณสลัดแขนนั้นทิ้งแล้วหนี", effects: { health: -15, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "crinoid_feather",
    archetypes: E,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["echinoderm.other"] },
    weight: 5,
    text: "กระแสน้ำแรงพัดผ่านโขดหินที่คุณเกาะอยู่",
    fact: "พลับพลึงทะเลกางแขนคล้ายขนนกดักจับอาหารในกระแสน้ำ และบางชนิดว่ายน้ำได้ด้วยการโบกแขน",
    choices: [
      { label: "กางแขนรับกระแสน้ำ", outcomes: [{ chance: 1, text: "แขนคล้ายขนนกกรองแพลงก์ตอนไว้ได้มากมาย", effects: { fullness: 30 } }] },
      { label: "โบกแขนว่ายไปที่สูงกว่า", outcomes: [{ chance: 1, text: "คุณว่ายขึ้นไปเกาะบนยอดปะการัง ที่กระแสน้ำพาอาหารมามากกว่า", effects: { fullness: 20, safety: 5 } }] },
    ],
  },
];
